import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    useGetStats,
    useGetMergedLinkForShootPending,
    useGetDownloadXLSheetReport,
    useGetTeamDetailsOfShoot,
    useGetBayTeams,
    useGetBayPerformance,
    useGetSkuSessionInfo,
    useGetSessionShootComplete,
    useGetShotSkusByCompany,
    useGetGuidelineAdherenceSkus,
    useGetOpenReshootSkus,
} from '../../hooks/usePhotoShootSession';
import { Button, DatePicker, Empty, message, Tooltip, Tag, Switch, Table, Space, Spin, Select } from 'antd';
import { CameraOutlined, ClockCircleOutlined, DownloadOutlined, ExportOutlined, EyeOutlined, FileSearchOutlined, QuestionCircleOutlined, ReloadOutlined, TeamOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { toPng } from 'html-to-image';
import * as FileSaver from 'file-saver';
import XLSX from 'sheetjs-style';
import './index.scss';
import { useNavigate } from 'react-router-dom';
import DynamicModal from '../../commonComponents/DynamicModal/DynamicModal';
import TeamInformation from './TeamInformation/TeamInformation';
import BayTeamsModal from './BayTeamsModal';
import BayHistogram, { ConsistencyDial } from './BayHistogram';
import OperatorBreaks from './OperatorBreaks';
import SkuSessionInfo from './SkuSessionInfo/SkuSessionInfo';
import CompanySkuList from './CompanySkuList';
import useLogout from '../../hooks/useLogout';
import moment from 'moment';
import stl from 'string-to-color';
import { getPhotoShootSessionDate } from '../../utils/photoShootSessionDate';

function formatRelativeTime(value) {
    if (!value) return '—';
    return moment.utc(value).local().startOf('seconds').fromNow();
}

function formatExactTime(value) {
    if (!value) return '';
    return moment.utc(value).local().format('YYYY-MM-DD HH:mm:ss');
}

function formatLocalClock(value) {
    if (!value) return '—';
    const local = moment.utc(value).local();
    if (!local.isValid() || local.year() === 1970) return '—';
    return local.format('LT');
}

function formatLocalDateTime(value) {
    if (!value) return '—';
    const local = moment.utc(value).local();
    if (!local.isValid() || local.year() === 1970) return '—';
    return local.format('D MMM YYYY, LT');
}

function hourHeading(iso, firstIso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    const time = date
        .toLocaleTimeString('en-US', { timeZone: 'Europe/Berlin', hour: 'numeric', hour12: true })
        .replace(/\s?(AM|PM)$/i, match => match.trim().toLowerCase());
    const day = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', day: 'numeric', month: 'short' }).format(date);
    const firstDay = firstIso
        ? new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', day: 'numeric', month: 'short' }).format(new Date(firstIso))
        : day;
    return day === firstDay ? time : `${time} ${day}`;
}

function formatCetClock(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime()) || date.getFullYear() <= 1971) return '—';
    return date
        .toLocaleTimeString('en-US', {
            timeZone: 'Europe/Berlin',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })
        .replace(/\s?(AM|PM)$/i, match => match.trim().toLowerCase());
}

function bayCode(record) {
    const email = getBayAccount(record)?.email || '';
    const local = email.split('@')[0];
    if (local) return local.toUpperCase();
    return record?.sessionId || '—';
}

function clientLabel(record, filteredCode) {
    if (filteredCode) return filteredCode;
    const counts = (record?.companyCodeCounts || []).filter(item => item?.companyCode && item.companyCode !== '—' && (item.count || 0) > 0);
    if (!counts.length) return '—';
    return counts.map(item => item.companyCode).join(', ');
}

function photographerLabel(record) {
    const names = getPhotographers(record).map(person => person.name || person.email).filter(Boolean);
    if (names.length) return names.join(', ');
    return getBayAccount(record)?.name || '—';
}

function instantMs(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime()) || date.getFullYear() <= 1971) return null;
    return date.getTime();
}

function formatDuration(ms) {
    const minutes = Math.max(0, Math.round(Math.abs(ms) / 60000));
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours <= 0) return `${mins}m`;
    return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

function shiftProgress(record, now = Date.now()) {
    const start = instantMs(record?.startTime);
    if (start == null) return null;
    const end = instantMs(record?.endTime);
    const lastScan = instantMs(record?.lastScannedAt);
    let stop = now;
    if (end != null) stop = Math.min(stop, end);
    if (!record?.isSessionAlive) stop = lastScan != null ? Math.min(stop, lastScan) : end != null ? stop : start;
    if (stop < start) stop = start;
    const since = now < start ? `in ${formatDuration(start - now)}` : formatDuration(stop - start);
    if (end == null || end <= start) return { since, percent: null };
    const percent = Math.round(Math.min(100, Math.max(0, ((stop - start) / (end - start)) * 100)));
    return { since, percent };
}

function shiftText(record, now) {
    const start = formatCetClock(record?.startTime);
    const end = formatCetClock(record?.endTime);
    const progress = shiftProgress(record, now);
    let range = '—';
    if (start !== '—' || end !== '—') {
        if (end === '—') range = record?.isSessionAlive ? `${start} – now` : start;
        else range = `${start} – ${end}`;
    }
    const pace = progress ? `${progress.since}${progress.percent != null ? ` · ${progress.percent}%` : ''}` : '';
    return { range, pace, since: progress?.since || '', percent: progress?.percent ?? null };
}

function listedHours(rows) {
    const seen = new Set();
    const hours = [];
    (rows || []).forEach(row => {
        (row.hours || []).forEach(hour => {
            if (!hour?.at || seen.has(hour.at)) return;
            seen.add(hour.at);
            hours.push(hour.at);
        });
    });
    hours.sort();
    return hours;
}

function BayShift({ record, now, prominent }) {
    const { range, pace, since, percent } = shiftText(record, now);
    if (!prominent) {
        return (
            <div className="bay-hours">
                <div>{range}</div>
                {pace ? <div className="bay-shift-progress">{pace}</div> : null}
            </div>
        );
    }
    return (
        <div className="bay-hours bay-hours-prominent">
            {percent != null && <div className="bay-shift-percent">{percent}%</div>}
            <div>{range}</div>
            {since ? <div className="bay-shift-progress">{since}</div> : null}
        </div>
    );
}

function HintTitle({ label, hint }) {
    if (!hint) return label;
    return (
        <span className="col-hint">
            {label}
            <Tooltip title={hint}>
                <QuestionCircleOutlined className="col-hint-icon" />
            </Tooltip>
        </span>
    );
}

function CompanyCodeCounts({ counts, session, onOpen }) {
    if (!counts?.length) return <span className="count-zero">—</span>;
    return (
        <div className="completed-details">
            {counts.map(item => (
                <Tag
                    key={item.companyCode}
                    color={stl(item.companyCode)}
                    className="company-count-tag"
                    onClick={() => onOpen?.({ companyCode: item.companyCode, session })}
                >
                    {item.companyCode}: <b>{item.count}</b>
                </Tag>
            ))}
        </div>
    );
}

function CountCell({ value, tone }) {
    const count = value || 0;
    const className = count === 0 ? 'count-zero' : tone ? `count-${tone}` : 'count-default';
    return <span className={className}>{count}</span>;
}

function reshootClientTitle(item) {
    const unknown = item?.unspecified ? `, ${item.unspecified} time unknown` : '';
    return `${item?.duringShift || 0} in shift, ${item?.outsideShift || 0} outside shift${unknown}`;
}

function ReshootClientTags({ rows }) {
    if (!rows?.length) return <span className="count-zero">None open</span>;
    return (
        <div className="completed-details">
            {rows.map(item => (
                <Tooltip key={item.companyCode} title={reshootClientTitle(item)}>
                    <span>
                        <Tag color={stl(item.companyCode)}>
                            {item.companyCode}: <b>{item.count}</b>
                        </Tag>
                    </span>
                </Tooltip>
            ))}
        </div>
    );
}

function ReshootSplitCell({ split, fallback }) {
    if (!split) return <CountCell value={fallback} tone="alert" />;
    if (!split.total) return <CountCell value={0} tone="alert" />;
    return (
        <div className="reshoot-split">
            <CountCell value={split.total} tone="alert" />
            <div className="reshoot-split-when">
                <span className="count-alert">{split.duringShift} in shift</span>
                <span className="count-warn">{split.outsideShift} outside</span>
                {split.unspecified > 0 ? <span>{split.unspecified} unknown</span> : null}
            </div>
            <ReshootClientTags rows={split.byClient} />
        </div>
    );
}

function sumReshootSplits(sessions) {
    const byClient = new Map();
    const totals = { duringShift: 0, outsideShift: 0, unspecified: 0, total: 0, byClient: [] };
    (sessions || []).forEach(session => {
        const split = session?.reshootSplit;
        if (!split) return;
        totals.duringShift += split.duringShift || 0;
        totals.outsideShift += split.outsideShift || 0;
        totals.unspecified += split.unspecified || 0;
        totals.total += split.total || 0;
        (split.byClient || []).forEach(item => {
            const row = byClient.get(item.companyCode) || {
                companyCode: item.companyCode,
                count: 0,
                duringShift: 0,
                outsideShift: 0,
                unspecified: 0,
            };
            row.count += item.count || 0;
            row.duringShift += item.duringShift || 0;
            row.outsideShift += item.outsideShift || 0;
            row.unspecified += item.unspecified || 0;
            byClient.set(item.companyCode, row);
        });
    });
    totals.byClient = [...byClient.values()].sort(
        (a, b) => b.count - a.count || (a.companyCode === '—') - (b.companyCode === '—') || a.companyCode.localeCompare(b.companyCode)
    );
    return totals;
}

function mixShotAndQc(shotDetails, qcDetails) {
    const rows = new Map();
    (shotDetails || []).forEach(item => {
        const productType = item?.productType || item?.companyCode || '—';
        rows.set(productType, { productType, shot: item?.count ?? 0, qc: 0 });
    });
    (qcDetails || []).forEach(item => {
        const productType = item?.productType || item?.companyCode || '—';
        const row = rows.get(productType) || { productType, shot: 0, qc: 0 };
        row.qc = item?.count ?? 0;
        rows.set(productType, row);
    });
    return [...rows.values()];
}

const BAY_TYPE_COLORS = {
    apparel: 'magenta',
    product: 'blue',
    scanning: 'cyan',
};

const USER_GROUP_COUNT = 2;

const ADHERENCE_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'in_guideline', label: 'Shot from guideline' },
    { key: 'out_of_guideline', label: 'Shot out of guideline' },
    { key: 'unshot', label: 'Not shot from guideline' },
];

const ADHERENCE_BUCKET_LABEL = {
    in_guideline: 'Shot from guideline',
    out_of_guideline: 'Shot out of guideline',
    unshot: 'Not shot from guideline',
};

const ADHERENCE_BUCKET_COLOR = {
    in_guideline: 'success',
    out_of_guideline: 'error',
    unshot: 'warning',
};

function adherenceFilterCount(adherence, skus, key) {
    if (skus) {
        if (key === 'all') return skus.length;
        return skus.filter(row => row.bucket === key).length;
    }
    const inGuideline = adherence?.inGuideline || 0;
    const outOfGuideline = adherence?.outOfGuideline || 0;
    const unshot = adherence?.unshotInGuideline || 0;
    if (key === 'in_guideline') return inGuideline;
    if (key === 'out_of_guideline') return outOfGuideline;
    if (key === 'unshot') return unshot;
    return inGuideline + outOfGuideline + unshot;
}

function GuidelineAdherencePanel({ loading, adherence, skus, fileName }) {
    const [filter, setFilter] = useState('all');
    const list = skus || [];
    const shown = filter === 'all' ? list : list.filter(row => row.bucket === filter);
    const exportExcel = () => {
        const rows = shown.map(row => ({
            Bucket: ADHERENCE_BUCKET_LABEL[row.bucket] || row.bucket || '',
            Barcode: row.barcode || '',
            SKU: row.skuName || '',
            Company: row.companyCode || '',
            'Product type': row.productType || '',
            Guideline: (row.guidelines || []).filter(Boolean).join(', '),
            'Raw QC': row.rawQc || '',
            'Shoot complete': formatLocalDateTime(row.shootCompletionTime),
        }));
        const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: 'No SKUs' }]);
        const buffer = XLSX.write({ Sheets: { SKUs: sheet }, SheetNames: ['SKUs'] }, { bookType: 'xlsx', type: 'array' });
        FileSaver.saveAs(
            new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            `${fileName}.xlsx`
        );
    };

    return (
        <div className="adherence-panel">
            <div className="company-sku-list-bar">
                <div className="adherence-filters">
                    <span className="adherence-minibatch">
                        Minibatch found: <b>{adherence?.minibatchFound ?? '—'}</b>
                    </span>
                    {ADHERENCE_FILTERS.map(item => (
                        <Button key={item.key} size="small" type={filter === item.key ? 'primary' : 'default'} onClick={() => setFilter(item.key)}>
                            {item.label} ({adherenceFilterCount(adherence, skus, item.key)})
                        </Button>
                    ))}
                </div>
                <Button icon={<DownloadOutlined />} onClick={exportExcel} disabled={loading || !shown.length}>
                    Export Excel
                </Button>
            </div>
            <Table
                rowKey="skuId"
                size="small"
                loading={loading}
                dataSource={shown}
                pagination={{ pageSize: 50, showSizeChanger: false }}
                scroll={{ x: 1180 }}
                locale={{ emptyText: <Empty description={loading ? 'Loading SKUs' : 'No SKUs'} /> }}
                columns={[
                    {
                        title: 'Bucket',
                        dataIndex: 'bucket',
                        width: 180,
                        render: value => <Tag color={ADHERENCE_BUCKET_COLOR[value] || 'default'}>{ADHERENCE_BUCKET_LABEL[value] || '—'}</Tag>,
                    },
                    { title: 'Barcode', dataIndex: 'barcode', width: 150, render: value => value || '—' },
                    { title: 'SKU', dataIndex: 'skuName', width: 200, ellipsis: true, render: value => value || '—' },
                    { title: 'Company', dataIndex: 'companyCode', width: 100, render: value => value || '—' },
                    { title: 'Product type', dataIndex: 'productType', width: 140, render: value => value || '—' },
                    {
                        title: 'Guideline',
                        dataIndex: 'guidelines',
                        width: 180,
                        render: guidelines => (guidelines || []).filter(Boolean).join(', ') || '—',
                    },
                    { title: 'Raw QC', dataIndex: 'rawQc', width: 100, render: value => value || '—' },
                    {
                        title: 'Shoot complete',
                        dataIndex: 'shootCompletionTime',
                        width: 170,
                        render: value => formatLocalDateTime(value),
                    },
                ]}
            />
        </div>
    );
}

function formatBayType(type) {
    if (!type) return null;
    return type.charAt(0).toUpperCase() + type.slice(1);
}

function getPhotographers(record) {
    return (record?.photographers || []).filter(person => person?.name || person?.email);
}

function getBayAccount(record) {
    if (!record?.user?.name && !record?.user?.email) return null;
    return record.user;
}

function listBayEmails(sessions) {
    const emails = new Set();
    (sessions || []).forEach(session => {
        const email = getBayAccount(session)?.email;
        if (email) emails.add(email);
    });
    return [...emails].sort((a, b) => a.localeCompare(b));
}

function filterSessionsByBayEmail(sessions, bayEmail) {
    const list = sessions || [];
    if (!bayEmail) return list;
    return list.filter(session => getBayAccount(session)?.email === bayEmail);
}

function listClientCodes(sessions) {
    const codes = new Set();
    (sessions || []).forEach(session => {
        (session?.companyCodeCounts || []).forEach(item => {
            if (item?.companyCode && (item.count || 0) > 0) codes.add(item.companyCode);
        });
    });
    return [...codes].sort((a, b) => a.localeCompare(b));
}

function filterSessionsByClient(sessions, companyCode) {
    const list = sessions || [];
    if (!companyCode) return list;
    return list.filter(session =>
        (session?.companyCodeCounts || []).some(item => item?.companyCode === companyCode && (item.count || 0) > 0)
    );
}

function getUserGroupKey(record) {
    return record?.user?.email || getPhotographers(record)[0]?.email || record?.sessionId || 'unknown';
}

function buildUserGroupIndex(sessions) {
    const map = new Map();
    let next = 0;
    (sessions || []).forEach(session => {
        const key = getUserGroupKey(session);
        if (!map.has(key)) {
            map.set(key, next);
            next += 1;
        }
    });
    return map;
}

function QcFinishedPanel({ loading, data }) {
    if (loading) {
        return (
            <div className="qc-modal-loading">
                <Spin />
            </div>
        );
    }
    const mix = mixShotAndQc(data?.shotDetails, data?.details);
    const shootTypeDetails = data?.shootTypeDetails || [];
    const pendingShootTypeDetails = data?.pendingShootTypeDetails || [];
    const items = data?.items || [];
    return (
        <div className="qc-modal">
            <div className="qc-modal-summary">
                <span>
                    Shot: <b>{data?.total ?? 0}</b>
                </span>
                <span>
                    QC done: <b>{data?.qcTotal ?? 0}</b>
                </span>
            </div>
            <div>
                <div className="qc-modal-label">By product type (QC / shot)</div>
                <div className="qc-modal-tags">
                    {mix.length ? (
                        mix.map(item => (
                            <Tag key={item.productType} color={item.qc >= item.shot && item.shot > 0 ? 'success' : 'default'}>
                                {item.productType}: <b>{item.qc}</b>
                                <span className="qc-of"> / {item.shot}</span>
                            </Tag>
                        ))
                    ) : (
                        <span className="count-zero">No shot items yet</span>
                    )}
                </div>
            </div>
            <div>
                <div className="qc-modal-label">Finished shoot types</div>
                <div className="qc-modal-tags">
                    {shootTypeDetails.length ? (
                        shootTypeDetails.map(item => (
                            <Tag key={item.shootType} color="blue">
                                {item.shootType}: <b>{item.count}</b>
                            </Tag>
                        ))
                    ) : (
                        <span className="count-zero">None finished yet</span>
                    )}
                </div>
            </div>
            <div>
                <div className="qc-modal-label">Pending shoot types</div>
                <div className="qc-modal-tags">
                    {pendingShootTypeDetails.length ? (
                        pendingShootTypeDetails.map(item => (
                            <Tag key={item.shootType} color="orange">
                                {item.shootType}: <b>{item.count}</b>
                            </Tag>
                        ))
                    ) : (
                        <span className="count-zero">Nothing pending</span>
                    )}
                </div>
            </div>
            <Table
                size="small"
                pagination={false}
                rowKey={(row, index) => `${row.ean}-${index}`}
                dataSource={items}
                locale={{ emptyText: <Empty description="No shot items" /> }}
                columns={[
                    { title: 'Barcode', dataIndex: 'ean', key: 'ean', width: 150 },
                    { title: 'Product type', dataIndex: 'productType', key: 'productType', width: 130 },
                    {
                        title: 'Finished types',
                        dataIndex: 'shootTypes',
                        key: 'shootTypes',
                        render: shootTypes =>
                            shootTypes?.length ? (
                                <div className="qc-modal-tags">
                                    {shootTypes.map(row => (
                                        <Tag key={row.shootType} color="blue">
                                            {row.shootType}
                                            {row.completedAt ? (
                                                <span className="qc-of"> · {formatLocalDateTime(row.completedAt)}</span>
                                            ) : null}
                                        </Tag>
                                    ))}
                                </div>
                            ) : (
                                <span className="count-zero">—</span>
                            ),
                    },
                    {
                        title: 'Pending types',
                        dataIndex: 'pendingShootTypes',
                        key: 'pendingShootTypes',
                        render: pendingTypes =>
                            pendingTypes?.length ? (
                                <div className="qc-modal-tags">
                                    {pendingTypes.map(row => (
                                        <Tag key={row.shootType} color="orange">
                                            {row.shootType}
                                        </Tag>
                                    ))}
                                </div>
                            ) : (
                                <span className="count-zero">—</span>
                            ),
                    },
                    {
                        title: 'QC finished',
                        dataIndex: 'shootCompletionTime',
                        key: 'shootCompletionTime',
                        width: 180,
                        render: value =>
                            value ? (
                                <Tooltip title={formatExactTime(value)}>
                                    <span>{formatLocalDateTime(value)}</span>
                                </Tooltip>
                            ) : (
                                <span className="count-zero">—</span>
                            ),
                    },
                ]}
            />
        </div>
    );
}

export default function LiveShoot() {
    const [now, setNow] = useState(() => Date.now());
    const [selectedDate, setSelectedDate] = useState(null);
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);
    const [bayEmail, setBayEmail] = useState(null);
    const [clientCode, setClientCode] = useState(null);
    const [autoReload, setAutoReload] = useState(false);
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);
    const [downloadingReshoots, setDownloadingReshoots] = useState(false);
    const [currentShootTeamHistory, setCurrentShootTeamHistory] = useState(null);
    const [bayTeamsOpen, setBayTeamsOpen] = useState(false);
    const [hourlyOpen, setHourlyOpen] = useState(false);
    const [shootingHourly, setShootingHourly] = useState(false);
    const hourlyRef = useRef(null);
    const [bayTeams, setBayTeams] = useState([]);
    const navigate = useNavigate();
    const { logout } = useLogout();
    const { data: stats, refetch, isFetching } = useGetStats(selectedDate, autoReload);
    const sessions = stats?.sessions;
    const minibatchFound = stats?.minibatchFound || 0;
    const shotByCompany = (stats?.shotByCompany || []).filter(item => !clientCode || item.companyCode === clientCode);
    const uniqueSkuTotal = shotByCompany.reduce((total, item) => total + (item.count || 0), 0);
    const shootComplete = stats?.shootComplete || 0;
    const shootCompleteByCompany = stats?.shootCompleteByCompany || [];
    const { data: bayPerformance, refetch: refetchPerformance } = useGetBayPerformance(selectedDate, autoReload);
    const GetMergedLinkForShootPendingMutation = useGetMergedLinkForShootPending();
    const GetDownloadXLSheetReportMutation = useGetDownloadXLSheetReport();
    const [skuSessionInfoModal, setSkuSessionInfoModal] = useState(false);
    const [skuSessionData, setSkuSessionData] = useState([]);
    const [filteredSkuInfoData, setFilteredSkuData] = useState([]);
    const [selectedSessionId, setSelectedSessionId] = useState('');
    const GetTeamDetailOfShoot = useGetTeamDetailsOfShoot();
    const getBayTeams = useGetBayTeams();

    const getSkuSessionInfo = useGetSkuSessionInfo();
    const getShotSkusByCompany = useGetShotSkusByCompany();
    const getOpenReshootSkus = useGetOpenReshootSkus();
    const getGuidelineAdherenceSkus = useGetGuidelineAdherenceSkus();
    const [companySkuView, setCompanySkuView] = useState(null);
    const [adherenceView, setAdherenceView] = useState(null);
    const getSessionShootComplete = useGetSessionShootComplete();
    const [qcModalSession, setQcModalSession] = useState(null);
    const [qcModalData, setQcModalData] = useState(null);
    const bayEmails = useMemo(() => listBayEmails(sessions), [sessions]);
    const clientCodes = useMemo(() => listClientCodes(sessions), [sessions]);
    const tableSessions = useMemo(
        () => filterSessionsByClient(filterSessionsByBayEmail(sessions, bayEmail), clientCode),
        [sessions, bayEmail, clientCode]
    );
    const userGroupIndex = useMemo(() => buildUserGroupIndex(tableSessions), [tableSessions]);
    const performanceBySession = useMemo(() => {
        const map = new Map();
        (bayPerformance?.bays || []).forEach(row => {
            if (row?.sessionId) map.set(row.sessionId, row);
        });
        return map;
    }, [bayPerformance]);
    const checkpointRows = useMemo(
        () =>
            (tableSessions || [])
                .map(session => ({
                    key: session?.sessionId || session?._id,
                    bay: bayCode(session),
                    client: clientLabel(session, clientCode),
                    photographer: photographerLabel(session),
                    startTime: session?.startTime,
                    endTime: session?.endTime,
                    isSessionAlive: session?.isSessionAlive,
                    lastScannedAt: session?.lastScannedAt,
                    hours: performanceBySession.get(session?.sessionId)?.hours || [],
                }))
                .sort((a, b) => a.bay.localeCompare(b.bay)),
        [tableSessions, clientCode, performanceBySession]
    );
    const checkpointColumns = useMemo(() => {
        const hourStarts = listedHours(checkpointRows);
        const first = hourStarts[0];
        return [
            { title: 'Bay', dataIndex: 'bay', width: 90, fixed: 'left' },
            { title: 'Client', dataIndex: 'client', width: 110 },
            { title: 'Photographer', dataIndex: 'photographer', width: 180 },
            {
                title: 'Hours',
                key: 'hours',
                width: 150,
                render: (_, record) => <BayShift record={record} now={now} />,
            },
            ...hourStarts.map(at => ({
                title: hourHeading(at, first),
                key: at,
                width: 72,
                align: 'right',
                render: (_, record) => {
                    const point = (record.hours || []).find(hour => hour.at === at);
                    if (!point) return <span className="count-zero">—</span>;
                    return (
                        <div className="hour-cell">
                            <div>{point.count}</div>
                            <div className="hour-added">+{point.added ?? 0}</div>
                            {point.reshoot > 0 ? <div className="hour-reshoot">+{point.reshoot}</div> : null}
                        </div>
                    );
                },
            })),
        ];
    }, [checkpointRows, now]);

    const downloadOpenReshoots = () => {
        setDownloadingReshoots(true);
        getOpenReshootSkus
            .mutateAsync([selectedDate])
            .then(data => {
                const rows = (data?.skus || []).map(row => ({
                    SKU: row.skuName || '',
                    Barcodes: (row.barcodes || []).filter(Boolean).join(', '),
                    'Reason 1': row.reason1 || '',
                    'Reason 2': row.reason2 || '',
                    'Reason 3': row.reason3 || '',
                }));
                const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: 'No open reshoots' }]);
                const buffer = XLSX.write({ Sheets: { Reshoots: sheet }, SheetNames: ['Reshoots'] }, { bookType: 'xlsx', type: 'array' });
                const day = selectedDate || getPhotoShootSessionDate();
                FileSaver.saveAs(
                    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                    `open-reshoots-${day}.xlsx`
                );
            })
            .catch(() => message.error('Could not download reshoot sheet'))
            .finally(() => setDownloadingReshoots(false));
    };

    const exportHourlyExcel = () => {
        const hourStarts = listedHours(checkpointRows);
        const first = hourStarts[0];
        const rows = checkpointRows.map(row => {
            const { range, pace } = shiftText(row, now);
            const record = {
                Bay: row.bay,
                Client: row.client,
                Photographer: row.photographer,
                Hours: range,
                Progress: pace,
            };
            hourStarts.forEach(at => {
                const heading = hourHeading(at, first);
                const point = (row.hours || []).find(hour => hour.at === at);
                record[`${heading} total`] = point ? point.count : '';
                record[`${heading} added`] = point ? point.added ?? 0 : '';
                record[`${heading} reshoot`] = point ? point.reshoot ?? 0 : '';
            });
            return record;
        });
        const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: 'No bays' }]);
        const buffer = XLSX.write({ Sheets: { Hours: sheet }, SheetNames: ['Hours'] }, { bookType: 'xlsx', type: 'array' });
        const day = selectedDate || getPhotoShootSessionDate();
        FileSaver.saveAs(
            new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            `hourly-totals-${day}.xlsx`
        );
    };

    const exportHourlyShot = async () => {
        const root = hourlyRef.current;
        if (!root) return;
        setShootingHourly(true);
        const wrap = root.querySelector('.ant-table-content');
        const previous = wrap
            ? { overflow: wrap.style.overflow, width: wrap.style.width, maxWidth: wrap.style.maxWidth }
            : null;
        try {
            if (wrap) {
                wrap.style.overflow = 'visible';
                wrap.style.maxWidth = 'none';
                wrap.style.width = 'max-content';
            }
            const width = Math.max(root.scrollWidth, wrap?.scrollWidth || 0);
            const height = Math.max(root.scrollHeight, root.offsetHeight);
            const dataUrl = await toPng(root, {
                cacheBust: true,
                pixelRatio: 2,
                backgroundColor: '#ffffff',
                width,
                height,
                style: { width: `${width}px`, height: `${height}px`, overflow: 'visible' },
            });
            const link = document.createElement('a');
            link.download = `hourly-totals-${selectedDate || getPhotoShootSessionDate()}.png`;
            link.href = dataUrl;
            link.click();
        } catch (error) {
            message.error('Could not export screenshot');
        } finally {
            if (wrap && previous) {
                wrap.style.overflow = previous.overflow;
                wrap.style.width = previous.width;
                wrap.style.maxWidth = previous.maxWidth;
            }
            setShootingHourly(false);
        }
    };

    const onChange = date => {
        setBayEmail(null);
        setClientCode(null);
        if (!!date) {
            setSelectedDate(date.format('YYYY-MM-DD'));
        } else {
            setSelectedDate(null);
        }
    };

    const HandlePendencyDataCreation = () => {
        GetMergedLinkForShootPendingMutation.mutateAsync([selectedDate])
            .then(() => { })
            .catch(console.log)
            .finally(() => {
                navigate('/guidelines');
            });
    };

    const HandleDownloadXLReport = () => {
        setIsDownloadingReport(true);
        GetDownloadXLSheetReportMutation.mutateAsync([selectedDate])
            .then(data => {
                var blob = new Blob([data.data], {
                    type: data.headers['content-type'],
                });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                let filename = data.headers['content-disposition'].split('filename=')[1].split('.')[0];

                link.setAttribute('download', `${filename}.xlsx`);
                document.body.appendChild(link);
                link.click();
                message.success('Report downloaded');
                setIsDownloadingReport(false);
            })
            .catch(async e => {
                let errorData = await e?.response.data.text();
                let responseMessage = JSON.parse(errorData)?.message;
                message.error(responseMessage || e?.response?.statusText);
                setIsDownloadingReport(false);
            })
            .finally(() => {
                setIsDownloadingReport(false);
            });
    };

    const checkDownloadReportDisability = () => {
        if (!selectedDate) {
            return true;
        }

        const currentSessionDate = getPhotoShootSessionDate();
        return selectedDate >= currentSessionDate;
    };

    const showBayTeams = () => {
        setBayTeamsOpen(true);
        setBayTeams([]);
        getBayTeams
            .mutateAsync([selectedDate])
            .then(data => {
                setBayTeams(data?.teams || []);
            })
            .catch(e => {
                message.error(e?.response?.data?.message || 'Could not load teams');
                setBayTeamsOpen(false);
                if (e?.response?.status === 401) logout();
            });
    };

    const showTeamDetail = sessionId => {
        GetTeamDetailOfShoot.mutateAsync([sessionId])
            .then(data => {
                setCurrentShootTeamHistory(data);
            })
            .catch(e => {
                message.error(e?.response?.data?.message || 'Could not load team');
                if (e?.response?.status === 401) logout();
            })
            .finally(() => { });
    };
    const handleSkuSessionInfoData = sessionId => {
        getSkuSessionInfo
            .mutateAsync([sessionId])
            .then(data => {
                setSkuSessionData(data.data);
                setFilteredSkuData(data.data);
            })
            .catch(e => {
                message.error(e?.response?.data?.message || 'Could not load items');
                setSkuSessionInfoModal(false);
                if (e?.response?.status === 401) logout();
            })
            .finally(() => { });
    };
    const handleSkuSessionInfo = sessionId => {
        setSkuSessionInfoModal(true);
        setSelectedSessionId(sessionId);
        handleSkuSessionInfoData(sessionId);
    };
    const handleCloseModal = () => {
        setSkuSessionInfoModal(false);
        setSelectedSessionId('');
    };
    const showQcDetails = session => {
        setQcModalSession(session);
        setQcModalData(null);
        getSessionShootComplete
            .mutateAsync([session?.sessionId])
            .then(response => {
                setQcModalData(response?.data || null);
            })
            .catch(e => {
                message.error(e?.response?.data?.message || 'Could not load finished items');
                setQcModalSession(null);
                if (e?.response?.status === 401) logout();
            });
    };
    const openCompanySkus = ({ companyCode, session }) => {
        const sessionId = session?.sessionId || '';
        const bayLabel = getBayAccount(session)?.email || '';
        setCompanySkuView({ companyCode, sessionId, bayLabel, rows: null });
        getShotSkusByCompany
            .mutateAsync([selectedDate, companyCode, sessionId])
            .then(data => {
                setCompanySkuView(current => {
                    if (!current || current.companyCode !== companyCode || current.sessionId !== sessionId) return current;
                    return { ...current, rows: data?.skus || [] };
                });
            })
            .catch(e => {
                message.error(e?.response?.data?.message || 'Could not load SKUs');
                setCompanySkuView(current => (current?.companyCode === companyCode && current?.sessionId === sessionId ? null : current));
                if (e?.response?.status === 401) logout();
            });
    };
    const openAdherence = session => {
        const sessionId = session?.sessionId || '';
        setAdherenceView({
            sessionId,
            bayLabel: bayCode(session),
            adherence: session?.guidelineAdherence || null,
            skus: null,
        });
        getGuidelineAdherenceSkus
            .mutateAsync([sessionId])
            .then(data => {
                setAdherenceView(current => {
                    if (!current || current.sessionId !== sessionId) return current;
                    return {
                        ...current,
                        adherence: {
                            ...(current.adherence || {}),
                            ...(data?.adherence || {}),
                            minibatchFound: data?.adherence?.minibatchFound ?? current.adherence?.minibatchFound,
                        },
                        skus: data?.skus || [],
                    };
                });
            })
            .catch(e => {
                message.error(e?.response?.data?.message || 'Could not load guideline SKUs');
                setAdherenceView(current => (current?.sessionId === sessionId ? null : current));
                if (e?.response?.status === 401) logout();
            });
    };
    const closeQcModal = () => {
        setQcModalSession(null);
        setQcModalData(null);
    };
    const handleSkuInfoSearch = e => {
        let data = [...skuSessionData];
        if (e.target.value) {
            const filteredData = data.filter(item => item.EAN?.includes(e.target.value));
            setFilteredSkuData(filteredData);
        } else {
            setFilteredSkuData(skuSessionData);
        }
    };

    const columns = [
        {
            title: <HintTitle label="Status" hint="Whether this bay is still live" />,
            dataIndex: 'isSessionAlive',
            key: 'isSessionAlive',
            width: 90,
            filters: [
                { text: 'Live', value: true },
                { text: 'Ended', value: false },
            ],
            onFilter: (value, record) => !!record.isSessionAlive === value,
            render: alive => (
                <Tag color={alive ? 'success' : 'default'} className="status-tag">
                    {alive ? 'Live' : 'Ended'}
                </Tag>
            ),
        },
        {
            title: <HintTitle label="Bay" hint="Apparel, product, or scanning bay" />,
            dataIndex: 'bayType',
            key: 'bayType',
            width: 110,
            filters: [
                { text: 'Apparel', value: 'apparel' },
                { text: 'Product', value: 'product' },
                { text: 'Scanning', value: 'scanning' },
            ],
            onFilter: (value, record) => record.bayType === value,
            render: type => {
                const label = formatBayType(type);
                if (!label) return '—';
                return <Tag color={BAY_TYPE_COLORS[type] || 'default'}>{label}</Tag>;
            },
        },
        {
            title: (
                <HintTitle
                    label="Hours"
                    hint="Scheduled start and end in CET. Time since start stops at the scheduled end, or at the last shot if the bay already closed."
                />
            ),
            key: 'bayHours',
            width: 170,
            render: (_, record) => <BayShift record={record} now={now} prominent />,
        },
        {
            title: (
                <HintTitle
                    label="Consistency"
                    hint="How evenly completions are spread across half-hour slots between this bay's start and end. 100 is perfectly even."
                />
            ),
            key: 'consistency',
            width: 110,
            render: (_, record) => <ConsistencyDial score={performanceBySession.get(record.sessionId)?.consistency} />,
        },
        {
            title: <HintTitle label="Photographer" hint="Who is shooting on this bay" />,
            key: 'photographer',
            width: 240,
            render: (_, record) => {
                const photographers = getPhotographers(record);
                const bay = getBayAccount(record);
                const photographerEmails = new Set(photographers.map(person => person.email).filter(Boolean));
                const showBayEmail = bay?.email && !photographerEmails.has(bay.email);

                return (
                    <div className="person-cell">
                        {photographers.length ? (
                            photographers.map((person, index) => (
                                <div key={person.email || person.name || index}>
                                    <div className="person-name">{person.name || person.email || '—'}</div>
                                    {person.name && person.email && <div className="person-sub">{person.email}</div>}
                                </div>
                            ))
                        ) : (
                            <div className="person-name">{bay?.name || '—'}</div>
                        )}
                        {showBayEmail && (
                            <Tooltip title="Bay login email">
                                <div className="person-sub">Bay: {bay.email}</div>
                            </Tooltip>
                        )}
                        {record?.sessionId && (
                            <Tooltip title="Bay ID">
                                <div className="person-id">{record.sessionId}</div>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            title: <HintTitle label="Guideline" hint="Merged guidelines this bay selected to shoot" />,
            key: 'guidelines',
            width: 220,
            render: (_, record) => {
                const guidelines = record?.guidelines || [];
                if (!guidelines.length) return '—';
                return (
                    <div className="person-cell">
                        {guidelines.map((guideline, index) => (
                            <div key={guideline.link || index} className="guideline-row">
                                <Tooltip title={guideline.link}>
                                    <div className="person-name">{guideline.name || guideline.link}</div>
                                </Tooltip>
                                {guideline.link && (
                                    <Tooltip title="Open guideline">
                                        <Button
                                            type="text"
                                            size="small"
                                            className="guideline-open"
                                            icon={<ExportOutlined />}
                                            aria-label="Open guideline"
                                            href={guideline.link}
                                            target="_blank"
                                            rel="noreferrer"
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: (
                <HintTitle
                    label="Guideline adherence"
                    hint="100 only when every SKU on this bay's guidelines was shot here and nothing outside those guidelines was shot. In = shot from the guideline. Out = shot, but not on the guideline. Unshot = on the guideline, not shot on this bay. Minibatch = unique SKUs the minibatch team found today on these guideline links, including a match while scanning another list."
                />
            ),
            key: 'guidelineAdherence',
            width: 200,
            sorter: (a, b) => (a.guidelineAdherence?.score ?? -1) - (b.guidelineAdherence?.score ?? -1),
            render: (_, record) => {
                const adherence = record?.guidelineAdherence;
                if (!adherence || (adherence.score == null && !adherence.minibatchFound)) return <span className="count-zero">—</span>;
                return (
                    <button type="button" className="adherence-cell" onClick={() => openAdherence(record)}>
                        <ConsistencyDial score={adherence.score} />
                        <span className="adherence-counts">
                            <span className="count-ok">In {adherence.inGuideline || 0}</span>
                            <span className={adherence.outOfGuideline ? 'count-alert' : 'count-zero'}>Out {adherence.outOfGuideline || 0}</span>
                            <span className={adherence.unshotInGuideline ? 'count-warn' : 'count-zero'}>Unshot {adherence.unshotInGuideline || 0}</span>
                            <span className={adherence.minibatchFound ? 'count-default' : 'count-zero'}>Minibatch {adherence.minibatchFound || 0}</span>
                        </span>
                    </button>
                );
            },
        },
        {
            title: <HintTitle label="Apparel & variants" hint="What is received by the photographer" />,
            className: 'planned-group',
            children: [
                {
                    title: <HintTitle label="Apparel" hint="What is received by the photographer" />,
                    dataIndex: 'apparelCount',
                    key: 'apparelCount',
                    width: 100,
                    align: 'right',
                    className: 'planned-cell',
                    sorter: (a, b) => (a.apparelCount || 0) - (b.apparelCount || 0),
                    render: value => <CountCell value={value} />,
                },
                {
                    title: <HintTitle label="Variants" hint="What is received by the photographer" />,
                    dataIndex: 'variantCount',
                    key: 'variantCount',
                    width: 100,
                    align: 'right',
                    className: 'planned-cell',
                    sorter: (a, b) => (a.variantCount || 0) - (b.variantCount || 0),
                    render: value => <CountCell value={value} />,
                },
            ],
        },
        {
            title: 'Progress',
            className: 'progress-group',
            children: [
                {
                    title: <HintTitle label="Waiting" hint="Scanned but not yet shoot done" />,
                    dataIndex: 'pending',
                    key: 'pending',
                    width: 100,
                    align: 'right',
                    sorter: (a, b) => (a.pending || 0) - (b.pending || 0),
                    render: value => <CountCell value={value} tone="warn" />,
                },
                {
                    title: (
                        <HintTitle
                            label="Reshoot"
                            hint="Reshoots still on this bay, by when Raw QC sent them and by client. In shift is between this bay's start and end. Outside is before the start or after the end. A reshoot this bay already finished is left out."
                        />
                    ),
                    dataIndex: 'reshoot',
                    key: 'reshoot',
                    width: 220,
                    align: 'right',
                    sorter: (a, b) => (a.reshootSplit?.total ?? a.reshoot ?? 0) - (b.reshootSplit?.total ?? b.reshoot ?? 0),
                    render: (value, record) => <ReshootSplitCell split={record.reshootSplit} fallback={value} />,
                },
                {
                    title: <HintTitle label="CC" hint="Pending in CC" />,
                    dataIndex: 'cc',
                    key: 'cc',
                    width: 80,
                    align: 'right',
                    sorter: (a, b) => (a.cc || 0) - (b.cc || 0),
                    render: value => <CountCell value={value} tone="warn" />,
                },
                {
                    title: <HintTitle label="Shot" hint="Photographer marked the shoot done" />,
                    dataIndex: 'completed',
                    key: 'completed',
                    width: 90,
                    align: 'right',
                    sorter: (a, b) => (a.completed || 0) - (b.completed || 0),
                    render: value => <CountCell value={value} tone="ok" />,
                },
                {
                    title: (
                        <HintTitle
                            label="Fully shot"
                            hint="Of the SKUs this bay marked shoot done, how many have the shoot finished. Every shoot type has a file, or Raw QC has completed the SKU."
                        />
                    ),
                    dataIndex: 'fullyShot',
                    key: 'fullyShot',
                    width: 120,
                    align: 'right',
                    sorter: (a, b) => (a.fullyShot || 0) - (b.fullyShot || 0),
                    render: (value, record) => (
                        <span>
                            <CountCell value={value} tone="ok" />
                            <span className="qc-of"> / {record?.completed || 0}</span>
                        </span>
                    ),
                },
                {
                    title: (
                        <HintTitle
                            label="QC done"
                            hint="Raw QC finished this SKU for the bay. Includes Shoot completed by bay: this bay finished its shoot types, and pending shoot types will be shot on another bay. No reshoot is sent."
                        />
                    ),
                    dataIndex: 'completedByRawQC',
                    key: 'completedByRawQC',
                    width: 130,
                    sorter: (a, b) => (a.completedByRawQC || 0) - (b.completedByRawQC || 0),
                    render: (value, record) => (
                        <div className="qc-cell">
                            <CountCell value={value} tone="ok" />
                            <Button type="link" size="small" className="row-btn" icon={<EyeOutlined />} onClick={() => showQcDetails(record)}>
                                Details
                            </Button>
                        </div>
                    ),
                },
            ],
        },
        {
            title: <HintTitle label="By company" hint="Unique SKUs this bay marked shoot done, counted by company code" />,
            dataIndex: 'companyCodeCounts',
            key: 'companyCodeCounts',
            width: 220,
            render: (counts, record) => <CompanyCodeCounts counts={counts} session={record} onOpen={openCompanySkus} />,
        },
        {
            title: <HintTitle label="What was shot" hint="Category wise shoot done" />,
            dataIndex: 'completedDetails',
            key: 'completedDetails',
            width: 240,
            render: details => {
                if (!details?.length) return <span className="count-zero">—</span>;
                return (
                    <div className="completed-details">
                        {details.map((item, key) => (
                            <Tag key={`${item?.productType || 'type'}-${key}`}>
                                {item?.productType || '—'}: <b>{item?.count ?? 0}</b>
                            </Tag>
                        ))}
                    </div>
                );
            },
        },
        {
            title: <HintTitle label="Pending at RawQC" hint="Shot but RawQC not yet done" />,
            dataIndex: 'pendingAtRawQC',
            key: 'pendingAtRawQC',
            width: 160,
            align: 'right',
            sorter: (a, b) => (a.pendingAtRawQC || 0) - (b.pendingAtRawQC || 0),
            render: (value, record) => (
                <span>
                    <CountCell value={value} tone="warn" />
                    <span className="qc-of"> / {record?.completed || 0}</span>
                </span>
            ),
        },
        {
            title: <HintTitle label="Last shot" hint="When the last item was photographed" />,
            dataIndex: 'lastScannedAt',
            key: 'lastScannedAt',
            width: 120,
            render: value =>
                value ? (
                    <Tooltip title={formatExactTime(value)}>
                        <span>{formatRelativeTime(value)}</span>
                    </Tooltip>
                ) : (
                    <span className="count-zero">—</span>
                ),
        },
        {
            title: '',
            key: 'actions',
            width: 180,
            fixed: 'right',
            render: (_, session) => (
                <Space size={6} className="row-actions">
                    <Button
                        className="row-btn row-btn-items"
                        size="small"
                        icon={<UnorderedListOutlined />}
                        onClick={() => handleSkuSessionInfo(session?.sessionId)}
                    >
                        Items
                    </Button>
                    <Button className="row-btn row-btn-team" size="small" icon={<TeamOutlined />} onClick={() => showTeamDetail(session?.sessionId)}>
                        Team
                    </Button>
                </Space>
            ),
        },
    ];

    const summary = useMemo(() => {
        const list = sessions || [];
        const sum = key => list.reduce((total, session) => total + (session?.[key] || 0), 0);
        const bayTypeCount = type => list.filter(session => session?.bayType === type).length;
        const adherence = list.reduce(
            (totals, session) => {
                const row = session?.guidelineAdherence;
                if (!row) return totals;
                totals.inGuideline += row.inGuideline || 0;
                totals.outOfGuideline += row.outOfGuideline || 0;
                totals.unshotInGuideline += row.unshotInGuideline || 0;
                return totals;
            },
            { inGuideline: 0, outOfGuideline: 0, unshotInGuideline: 0 }
        );
        const adherenceDenom = adherence.inGuideline + adherence.outOfGuideline + adherence.unshotInGuideline;
        return {
            bays: list.length,
            live: list.filter(session => session?.isSessionAlive).length,
            ended: list.filter(session => !session?.isSessionAlive).length,
            apparelBays: bayTypeCount('apparel'),
            productBays: bayTypeCount('product'),
            scanningBays: bayTypeCount('scanning'),
            shot: sum('completed'),
            waiting: sum('pending'),
            cc: sum('cc'),
            qc: sum('completedByRawQC'),
            pendingAtRawQC: sum('pendingAtRawQC'),
            plannedApparel: sum('apparelCount'),
            plannedVariants: sum('variantCount'),
            adherenceIn: adherence.inGuideline,
            adherenceOut: adherence.outOfGuideline,
            adherenceUnshot: adherence.unshotInGuideline,
            adherenceScore: adherenceDenom ? Math.round((adherence.inGuideline / adherenceDenom) * 100) : null,
            reshootOpen: sumReshootSplits(list),
        };
    }, [sessions]);
    const dateLabel = selectedDate ? moment(selectedDate).format('D MMM YYYY') : 'today';
    const downloadDisabled = checkDownloadReportDisability();

    return (
        <div className="live-shoot-page">
            <div className="toolbar">
                <div className="toolbar-left">
                    <div>
                        <h1 className="page-title">Live Shoot</h1>
                        <p className="page-subtitle">Bays for {dateLabel}</p>
                    </div>
                    <DatePicker onChange={onChange} allowClear placeholder="Pick a date" />
                    <Select
                        className="bay-email-filter"
                        allowClear
                        showSearch
                        placeholder="Client"
                        value={clientCode}
                        onChange={value => setClientCode(value || null)}
                        options={clientCodes.map(code => ({ value: code, label: code }))}
                        optionFilterProp="label"
                    />
                    <Select
                        className="bay-email-filter"
                        allowClear
                        showSearch
                        placeholder="Bay email"
                        value={bayEmail}
                        onChange={value => setBayEmail(value || null)}
                        options={bayEmails.map(email => ({ value: email, label: email }))}
                        optionFilterProp="label"
                    />
                </div>
                <div className="toolbar-stats">
                    <div className="stat-group">
                        <div className="stat-group-head">
                            <div className="stat-group-title">
                                <HintTitle
                                    label="Open reshoots"
                                    hint="Reshoots still on a bay. A reshoot the bay already finished is left out. In shift means Raw QC sent it between that bay's start and end. Outside means before the start or after the end. Each client chip is that client's SKUs still waiting on a bay."
                                />
                            </div>
                            <Button
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={downloadOpenReshoots}
                                loading={downloadingReshoots}
                                disabled={!summary.reshootOpen.total}
                            >
                                Download
                            </Button>
                        </div>
                        <div className="stat-group-chips">
                            <div className="stat-chip stat-chip-alert">
                                <span className="stat-label">In shift</span>
                                <strong>{summary.reshootOpen.duringShift}</strong>
                            </div>
                            <div className="stat-chip stat-chip-warn">
                                <span className="stat-label">Outside shift</span>
                                <strong>{summary.reshootOpen.outsideShift}</strong>
                            </div>
                            {summary.reshootOpen.unspecified > 0 ? (
                                <div className="stat-chip">
                                    <span className="stat-label">Time unknown</span>
                                    <strong>{summary.reshootOpen.unspecified}</strong>
                                </div>
                            ) : null}
                            <div className="stat-chip">
                                <span className="stat-label">Open</span>
                                <strong>{summary.reshootOpen.total}</strong>
                            </div>
                        </div>
                        <ReshootClientTags rows={summary.reshootOpen.byClient} />
                    </div>
                    <div className="stat-group">
                        <div className="stat-group-title">Bays</div>
                        <div className="stat-group-chips">
                            <div className="stat-chip stat-chip-live">
                                <span className="stat-label">Live</span>
                                <strong>{summary.live}</strong>
                            </div>
                            <div className="stat-chip">
                                <span className="stat-label">Ended</span>
                                <strong>{summary.ended}</strong>
                            </div>
                            <div className="stat-chip">
                                <span className="stat-label">Total</span>
                                <strong>{summary.bays}</strong>
                            </div>
                            <div className="stat-chip">
                                <span className="stat-label">Apparel</span>
                                <strong>{summary.apparelBays}</strong>
                            </div>
                            <div className="stat-chip">
                                <span className="stat-label">Product</span>
                                <strong>{summary.productBays}</strong>
                            </div>
                            <div className="stat-chip">
                                <span className="stat-label">Scanning</span>
                                <strong>{summary.scanningBays}</strong>
                            </div>
                        </div>
                    </div>
                    <div className="stat-group">
                        <div className="stat-group-title">
                            <HintTitle
                                label="Unique SKUs"
                                hint="SKUs marked shoot done today, once across all bays. A SKU shot on more than one bay still counts as one. This is the Unique SKUs count on Revenue."
                            />
                        </div>
                        <div className="stat-group-chips">
                            {shotByCompany.length ? (
                                [
                                    ...(clientCode ? [] : [{ companyCode: 'All', count: uniqueSkuTotal, total: true }]),
                                    ...shotByCompany,
                                ].map(item => (
                                    <button
                                        type="button"
                                        className="stat-chip stat-chip-ok stat-chip-button"
                                        key={item.companyCode}
                                        onClick={item.total ? undefined : () => openCompanySkus({ companyCode: item.companyCode })}
                                    >
                                        <span className="stat-label">{item.companyCode}</span>
                                        <strong>{item.count}</strong>
                                    </button>
                                ))
                            ) : (
                                <span className="count-zero">None shot yet</span>
                            )}
                        </div>
                    </div>
                    <div className="stat-group">
                        <div className="stat-group-title">
                            <HintTitle
                                label="Fully shot"
                                hint="Unique SKUs shot today whose shoot is finished, by client. Every shoot type has a file, or Raw QC has completed the SKU. Counted once across all bays."
                            />
                        </div>
                        <div className="stat-group-chips">
                            {shootCompleteByCompany.length ? (
                                shootCompleteByCompany.map(item => (
                                    <div className="stat-chip stat-chip-ok" key={item.companyCode}>
                                        <span className="stat-label">{item.companyCode}</span>
                                        <strong>{item.count}</strong>
                                    </div>
                                ))
                            ) : (
                                <span className="count-zero">None fully shot yet</span>
                            )}
                        </div>
                    </div>
                    <div className="stat-group">
                        <div className="stat-group-title">Progress</div>
                        <div className="stat-group-chips">
                            <div className="stat-chip stat-chip-ok">
                                <span className="stat-label">Shot</span>
                                <strong>{summary.shot}</strong>
                            </div>
                            <div className="stat-chip stat-chip-ok">
                                <span className="stat-label">
                                    <HintTitle
                                        label="Fully shot"
                                        hint="Unique SKUs shot today whose shoot is finished. Every shoot type has a file, or Raw QC has completed the SKU. Counted once across all bays."
                                    />
                                </span>
                                <strong>{shootComplete}</strong>
                            </div>
                            <div className="stat-chip stat-chip-ok">
                                <span className="stat-label">QC done</span>
                                <strong>{summary.qc}</strong>
                            </div>
                            <div className="stat-chip stat-chip-warn">
                                <span className="stat-label">Pending RawQC</span>
                                <strong>{summary.pendingAtRawQC}</strong>
                            </div>
                            <div className="stat-chip stat-chip-warn">
                                <span className="stat-label">Waiting</span>
                                <strong>{summary.waiting}</strong>
                            </div>
                            <div className="stat-chip stat-chip-alert">
                                <span className="stat-label">
                                    <HintTitle
                                        label="Reshoot"
                                        hint="Reshoots still on a bay. A reshoot the bay already finished is left out."
                                    />
                                </span>
                                <strong>{summary.reshootOpen.total}</strong>
                            </div>
                            <div className="stat-chip stat-chip-warn">
                                <span className="stat-label">CC</span>
                                <strong>{summary.cc}</strong>
                            </div>
                        </div>
                    </div>
                    <div className="stat-group">
                        <div className="stat-group-title">Planned</div>
                        <div className="stat-group-chips">
                            <div className="stat-chip">
                                <span className="stat-label">Apparel</span>
                                <strong>{summary.plannedApparel}</strong>
                            </div>
                            <div className="stat-chip">
                                <span className="stat-label">Variants</span>
                                <strong>{summary.plannedVariants}</strong>
                            </div>
                        </div>
                    </div>
                    <div className="stat-group">
                        <div className="stat-group-title">
                            <HintTitle
                                label="Guideline adherence"
                                hint="Across all bays: shot from the selected guidelines, divided by shot-from-guideline plus shot-outside plus guideline SKUs not shot on that bay."
                            />
                        </div>
                        <div className="stat-group-chips">
                            <div className="stat-chip">
                                <span className="stat-label">Score</span>
                                <strong>{summary.adherenceScore == null ? '—' : summary.adherenceScore}</strong>
                            </div>
                            <div className="stat-chip stat-chip-ok">
                                <span className="stat-label">In guideline</span>
                                <strong>{summary.adherenceIn}</strong>
                            </div>
                            <div className="stat-chip stat-chip-alert">
                                <span className="stat-label">Out of guideline</span>
                                <strong>{summary.adherenceOut}</strong>
                            </div>
                            <div className="stat-chip stat-chip-warn">
                                <span className="stat-label">Unshot</span>
                                <strong>{summary.adherenceUnshot}</strong>
                            </div>
                            <div className="stat-chip">
                                <span className="stat-label">
                                    <HintTitle
                                        label="Minibatch found"
                                        hint="Unique SKUs the minibatch team found today on any bay's guideline links. A SKU matched on two bays counts once."
                                    />
                                </span>
                                <strong>{minibatchFound}</strong>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="toolbar-actions">
                    <label className="auto-refresh">
                        <span>Keep updating</span>
                        <Switch checked={autoReload} onChange={setAutoReload} size="small" />
                    </label>
                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={() => {
                            refetch();
                            refetchPerformance();
                        }}
                        loading={isFetching}
                    >
                        Refresh
                    </Button>
                    <Button icon={<TeamOutlined />} onClick={showBayTeams} loading={getBayTeams.isLoading}>
                        Teams
                    </Button>
                    <Button icon={<ClockCircleOutlined />} onClick={() => setHourlyOpen(true)}>
                        Hourly totals
                    </Button>
                    <Tooltip title={downloadDisabled ? 'Pick a past date to download a report' : 'Download Excel report'}>
                        <span>
                            <Button icon={<DownloadOutlined />} onClick={() => HandleDownloadXLReport()} loading={isDownloadingReport} disabled={downloadDisabled}>
                                Report
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Open the pending shoot list for this date">
                        <Button icon={<FileSearchOutlined />} onClick={() => HandlePendencyDataCreation()} loading={GetMergedLinkForShootPendingMutation.isLoading}>
                            Pending list
                        </Button>
                    </Tooltip>
                </div>
            </div>
            <Table
                className="live-shoot-table"
                columns={columns}
                dataSource={tableSessions}
                rowKey={record => record?.sessionId || record?._id}
                loading={isFetching}
                size="middle"
                bordered
                pagination={false}
                sticky
                scroll={{ x: 2930 }}
                expandable={{
                    expandedRowRender: record => {
                        const performance = performanceBySession.get(record.sessionId);
                        return (
                            <div>
                                <BayHistogram histogram={performance?.histogram} timeZone="Europe/Berlin" zoneLabel="CET" />
                                <OperatorBreaks operators={performance?.operators} />
                            </div>
                        );
                    },
                    rowExpandable: record => {
                        const performance = performanceBySession.get(record.sessionId);
                        return (performance?.histogram || []).length > 0 || (performance?.operators || []).length > 0;
                    },
                }}
                locale={{
                    emptyText: <Empty description={bayEmail ? `No shoots for ${bayEmail}` : `No shoots for ${dateLabel}`} />,
                }}
                rowClassName={record => {
                    const group = userGroupIndex.get(getUserGroupKey(record)) ?? 0;
                    return `user-group-${group % USER_GROUP_COUNT}`;
                }}
            />

            {hourlyOpen && (
                <DynamicModal
                    title={`Hourly totals (${dateLabel})`}
                    open={hourlyOpen}
                    closable={true}
                    close={() => setHourlyOpen(false)}
                    width={'92%'}
                    height={'75vh'}
                    overflow="auto"
                    component={
                        <div className="checkpoint-board">
                            <div className="checkpoint-actions">
                                <Button icon={<DownloadOutlined />} onClick={exportHourlyExcel}>
                                    Excel
                                </Button>
                                <Button icon={<CameraOutlined />} onClick={exportHourlyShot} loading={shootingHourly}>
                                    Screenshot
                                </Button>
                            </div>
                            <div ref={hourlyRef}>
                                <p className="checkpoint-note">Each hour shows the running total, the shots added, and reshoots in red. Times are CET.</p>
                                <Table
                                    className="checkpoint-table"
                                    columns={checkpointColumns}
                                    dataSource={checkpointRows}
                                    rowKey="key"
                                    loading={isFetching}
                                    size="small"
                                    bordered
                                    pagination={false}
                                    scroll={{ x: 'max-content' }}
                                    locale={{ emptyText: <Empty description={`No bays for ${dateLabel}`} /> }}
                                />
                            </div>
                        </div>
                    }
                />
            )}
            {bayTeamsOpen && (
                <DynamicModal
                    title={`Teams on bays (${dateLabel})`}
                    open={bayTeamsOpen}
                    closable={true}
                    close={() => setBayTeamsOpen(false)}
                    width={'92%'}
                    height={'75vh'}
                    overflow="auto"
                    component={<BayTeamsModal loading={getBayTeams.isLoading} rows={bayTeams} dateLabel={dateLabel} />}
                />
            )}
            {!!currentShootTeamHistory && (
                <DynamicModal
                    title="Team on this bay"
                    open={!!currentShootTeamHistory}
                    closable={true}
                    close={() => {
                        setCurrentShootTeamHistory(null);
                    }}
                    width={'50%'}
                    height={'70%'}
                    overflow="auto"
                    style={{
                        top: '5%',
                    }}
                    component={<TeamInformation teamHistory={currentShootTeamHistory?.teamInfoHistory || []} />}
                />
            )}
            {skuSessionInfoModal && (
                <DynamicModal
                    title={selectedSessionId ? `Items on this bay (${selectedSessionId})` : 'Items on this bay'}
                    open={skuSessionInfoModal}
                    closable={true}
                    close={() => {
                        setSkuSessionInfoModal(false);
                    }}
                    width={'90%'}
                    height={'80vh'}
                    overflow="auto"
                    style={{
                        top: '5%',
                    }}
                    component={
                        <SkuSessionInfo skuSessionInfoData={filteredSkuInfoData} handleBtnClick={handleCloseModal} handleSearch={handleSkuInfoSearch} />
                    }
                />
            )}
            {!!adherenceView && (
                <DynamicModal
                    title={`Guideline adherence${adherenceView.bayLabel ? ` · ${adherenceView.bayLabel}` : ''}`}
                    open={!!adherenceView}
                    closable={true}
                    close={() => setAdherenceView(null)}
                    width={'90%'}
                    height={'75vh'}
                    overflow="auto"
                    component={
                        <GuidelineAdherencePanel
                            loading={adherenceView.skus == null}
                            adherence={adherenceView.adherence}
                            skus={adherenceView.skus}
                            fileName={`guideline-adherence-${adherenceView.bayLabel || adherenceView.sessionId}-${selectedDate || 'today'}`}
                        />
                    }
                />
            )}
            {!!companySkuView && (
                <DynamicModal
                    title={`${companySkuView.companyCode}${companySkuView.bayLabel ? ` · ${companySkuView.bayLabel}` : ' · all bays'}`}
                    open={!!companySkuView}
                    closable={true}
                    close={() => setCompanySkuView(null)}
                    width={'90%'}
                    height={'75vh'}
                    overflow="auto"
                    component={
                        <CompanySkuList
                            rows={companySkuView.rows}
                            loading={companySkuView.rows == null}
                            fileName={`shot-skus-${companySkuView.companyCode}-${companySkuView.bayLabel || 'all-bays'}-${selectedDate || 'today'}`}
                        />
                    }
                />
            )}
            {!!qcModalSession && (
                <DynamicModal
                    title={`Shot items${qcModalSession?.sessionId ? ` (${qcModalSession.sessionId})` : ''}`}
                    open={!!qcModalSession}
                    closable={true}
                    close={closeQcModal}
                    width={'80%'}
                    height={'70vh'}
                    overflow="auto"
                    component={<QcFinishedPanel loading={getSessionShootComplete.isLoading && !qcModalData} data={qcModalData} />}
                />
            )}
        </div>
    );
}
